import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, Clock, ChevronDown, ChevronRight } from 'react-feather';
import { apiClient } from '../shared/api/client';
import PageHeader from '../components/PageHeader';

interface CategoryStats {
  id: number;
  name: string;
  color?: string | null;
  icon?: string | null;
  parentId?: number | null;
  monthly: { [month: string]: number };
  total: number;
  monthlyAverage: number;
}

interface StatsResponse {
  months: string[];
  monthCount: number;
  categories: CategoryStats[];
  tree?: (CategoryStats & { children?: { id: number; name: string; color?: string; monthlyAverage: number }[] })[];
  tags?: {
    id: number;
    name: string;
    color?: string | null;
    monthly: { [month: string]: number };
    total: number;
    monthlyAverage: number;
    topItems: { name: string; monthlyAverage: number; count: number }[];
  }[];
  normalized?: {
    monthlyExpenses: number;
    monthlyIncome: number;
    headroom: number;
    reserveMonthlyTotal: number;
    reserves: { description: string; recurringType: string; expectedAmount: number; monthlyReserve: number }[];
  };
  uncategorized: { monthly: { [month: string]: number }; total: number; monthlyAverage: number };
  summary: {
    income: { monthly: { [month: string]: number }; monthlyAverage: number };
    expenses: { monthly: { [month: string]: number }; monthlyAverage: number };
    netMonthlyAverage: number;
  };
}

const UNCATEGORIZED_ID = -1;

const AnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [expandedTags, setExpandedTags] = useState<Set<number>>(new Set());
  const [expandedChildren, setExpandedChildren] = useState<Set<number>>(new Set());

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/stats/categories?months=12');
        setStats(response as any);
      } catch (err) {
        console.error('Failed to load stats:', err);
        setError('Kunde inte hämta statistiken. Försök igen.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(amount);

  const formatMonth = (key: string) => {
    const [y, m] = key.split('-');
    const names = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
    return `${names[parseInt(m) - 1]} ${y.slice(2)}`;
  };

  // Rows: categories with data or none, plus the uncategorized bucket
  const rows = useMemo(() => {
    if (!stats) return [];
    const source = stats.tree && stats.tree.length > 0 ? stats.tree : stats.categories;
    const catRows = source.map(c => ({ ...c, isUncategorized: false }));
    const uncatRow = {
      id: UNCATEGORIZED_ID,
      name: 'Okategoriserat',
      color: '#9CA3AF',
      icon: null,
      parentId: null,
      monthly: stats.uncategorized.monthly,
      total: stats.uncategorized.total,
      monthlyAverage: stats.uncategorized.monthlyAverage,
      topItems: (stats.uncategorized as any).topItems || [],
      isUncategorized: true
    };
    return [...catRows, uncatRow].sort((a, b) => b.monthlyAverage - a.monthlyAverage);
  }, [stats]);

  const excludedSum = useMemo(() => {
    return rows.filter(r => excluded.has(r.id)).reduce((sum, r) => sum + r.monthlyAverage, 0);
  }, [rows, excluded]);

  const toggleExclude = (id: number) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader mode="title" title="Analys" />
        <div className="page-content flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const baseExpenses = stats?.normalized?.monthlyExpenses ?? stats?.summary.expenses.monthlyAverage ?? 0;
  const adjustedExpenses = baseExpenses - excludedSum;
  const headroom = (stats?.summary.income.monthlyAverage || 0) - adjustedExpenses;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader mode="title" title="Analys" />

      <div className="page-content px-4 py-6">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-lg">
            <span className="text-red-800 text-sm">{error}</span>
          </div>
        )}

        {stats && (
          <>
            <p className="text-gray-600 mb-4">
              Snitt per månad baserat på {stats.monthCount} hel{stats.monthCount !== 1 ? 'a' : ''} månad{stats.monthCount !== 1 ? 'er' : ''}
              {stats.monthCount < 12 && ' — växer till 12 allt eftersom historik samlas. Pågående och ofullständiga månader visas i listorna men ingår inte i snittet.'}
            </p>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                <p className="text-xs text-gray-600 mb-1">Inkomst/mån</p>
                <p className="text-base font-bold text-green-600">{formatAmount(stats.summary.income.monthlyAverage)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                <p className="text-xs text-gray-600 mb-1">Normalkostnad/mån</p>
                <p className="text-base font-bold text-red-600">{formatAmount(stats.normalized?.monthlyExpenses ?? stats.summary.expenses.monthlyAverage)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                <p className="text-xs text-gray-600 mb-1">Utrymme/mån</p>
                <p className={`text-base font-bold ${(stats.normalized?.headroom ?? stats.summary.netMonthlyAverage) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatAmount(stats.normalized?.headroom ?? stats.summary.netMonthlyAverage)}
                </p>
              </div>
            </div>

            {stats.normalized && stats.normalized.reserveMonthlyTotal > 0 && (
              <p className="text-xs text-gray-500 mb-6 -mt-3 flex items-center flex-wrap">
                <Clock size={12} className="mr-1.5 flex-shrink-0" /> I normalkostnaden är års- och kvartalsposter utslagna per månad:{' '}
                {stats.normalized.reserves.map(r => `${r.description.split('→')[0].trim()} ${formatAmount(r.monthlyReserve)}/mån`).join(' · ')}
              </p>
            )}

            {/* What-if calculator */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center"><Info size={14} className="mr-1.5" />Vad händer om jag drar ner?</h3>
              <p className="text-sm text-blue-800 mb-3">
                Bocka ur kategorier nedan (t.ex. alkohol och tobak) för att se hur din månadskostnad
                och ditt utrymme förändras — t.ex. inför ett bilköp eller en leasing.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600 mb-1">Justerad månadskostnad</p>
                  <p className="text-lg font-bold text-gray-900">{formatAmount(adjustedExpenses)}</p>
                  {excludedSum > 0 && (
                    <p className="text-xs text-green-600">−{formatAmount(excludedSum)} borträknat</p>
                  )}
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600 mb-1">Utrymme/mån</p>
                  <p className={`text-lg font-bold ${headroom >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatAmount(headroom)}
                  </p>
                </div>
              </div>
            </div>

            {/* Category rows */}
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Utgifter per kategori</h2>
            <div className="space-y-2 mb-8">
              {rows.map(row => {
                const isExcluded = excluded.has(row.id);
                const isExpanded = expanded.has(row.id);
                const maxMonthly = Math.max(...stats.months.map(m => row.monthly[m] || 0), 1);
                return (
                  <div key={row.id} className={`bg-white rounded-xl shadow-sm overflow-hidden ${isExcluded ? 'opacity-60' : ''}`}>
                    <div className="flex items-center p-4">
                      <input
                        type="checkbox"
                        checked={!isExcluded}
                        onChange={() => toggleExclude(row.id)}
                        className="mr-3 h-4 w-4"
                        title={isExcluded ? 'Räkna med igen' : 'Räkna bort ur kalkylen'}
                      />
                      <button onClick={() => toggleExpand(row.id)} className="flex-1 flex items-center justify-between text-left">
                        <div className="flex items-center">
                          <span
                            className="w-3 h-3 rounded-full mr-3"
                            style={{ backgroundColor: row.color || '#9CA3AF' }}
                          ></span>
                          <span className={`font-medium ${isExcluded ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                            {row.name}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{formatAmount(row.monthlyAverage)}<span className="text-xs font-normal text-gray-500">/mån</span></p>
                          <p className="text-xs text-gray-500">{formatAmount(row.total)} totalt · {isExpanded ? 'dölj ▲' : 'per månad ▼'}</p>
                        </div>
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
                        {!((row as any).children && (row as any).children.length > 0) && (row as any).topItems && (row as any).topItems.length > 0 && (
                          <div className="mb-3">
                            <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Största posterna · snitt/mån</p>
                            {(row as any).topItems.map((item: any, idx: number) => (
                              <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                                <span className="text-gray-600 truncate">{item.name}</span>
                                <span className={`ml-3 flex-shrink-0 ${item.monthlyAverage < 0 ? 'text-green-600' : 'text-gray-800'}`}>
                                  {item.monthlyAverage < 0 ? '−' : ''}{formatAmount(Math.abs(item.monthlyAverage))}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {(row as any).children && (row as any).children.length > 0 && (
                          <div className="mb-3">
                            {(row as any).children.filter((c: any) => Math.abs(c.monthlyAverage) > 0).map((c: any) => {
                              const childOpen = expandedChildren.has(c.id);
                              const childItems = c.topItems || [];
                              return (
                                <div key={c.id} className="border-b border-gray-100 last:border-0">
                                  <button
                                    onClick={() => setExpandedChildren(prev => {
                                      const next = new Set(prev);
                                      if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                                      return next;
                                    })}
                                    className={`w-full flex items-center justify-between text-sm py-1.5 ${childItems.length > 0 ? 'cursor-pointer hover:bg-gray-100 rounded' : 'cursor-default'}`}
                                  >
                                    <span className="flex items-center min-w-0">
                                      {childItems.length > 0 ? (
                                        childOpen ? <ChevronDown size={12} className="mr-1 text-gray-400 flex-shrink-0" /> : <ChevronRight size={12} className="mr-1 text-gray-400 flex-shrink-0" />
                                      ) : (
                                        <span className="w-[12px] mr-1 flex-shrink-0"></span>
                                      )}
                                      <span className="w-2 h-2 rounded-full mr-2 flex-shrink-0" style={{ backgroundColor: c.color || '#9CA3AF' }}></span>
                                      <span className="text-gray-700 truncate">{c.name}</span>
                                    </span>
                                    <span className="text-gray-800 font-medium flex-shrink-0 ml-3">{formatAmount(c.monthlyAverage)}/mån</span>
                                  </button>
                                  {childOpen && childItems.length > 0 && (
                                    <div className="ml-4 pl-3 border-l border-gray-200 mb-1">
                                      {childItems.map((item: any, idx: number) => (
                                        <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                                          <span className="text-gray-500 truncate">{item.name}</span>
                                          <span className={`ml-3 flex-shrink-0 ${item.monthlyAverage < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                                            {item.monthlyAverage < 0 ? '−' : ''}{formatAmount(Math.abs(item.monthlyAverage))}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {(row as any).ownMonthlyAverage > 0.005 && (() => {
                              const ownKey = -(1000 + row.id);
                              const ownOpen = expandedChildren.has(ownKey);
                              const ownItems = (row as any).ownTopItems || [];
                              return (
                                <div className="border-b border-gray-100 last:border-0">
                                  <button
                                    onClick={() => ownItems.length > 0 && setExpandedChildren(prev => {
                                      const next = new Set(prev);
                                      if (next.has(ownKey)) next.delete(ownKey); else next.add(ownKey);
                                      return next;
                                    })}
                                    className={`w-full flex items-center justify-between text-sm py-1.5 ${ownItems.length > 0 ? 'cursor-pointer hover:bg-gray-100 rounded' : 'cursor-default'}`}
                                  >
                                    <span className="flex items-center min-w-0">
                                      {ownItems.length > 0 ? (
                                        ownOpen ? <ChevronDown size={12} className="mr-1 text-gray-400 flex-shrink-0" /> : <ChevronRight size={12} className="mr-1 text-gray-400 flex-shrink-0" />
                                      ) : (
                                        <span className="w-[12px] mr-1 flex-shrink-0"></span>
                                      )}
                                      <span className="w-2 h-2 rounded-full mr-2 flex-shrink-0 bg-gray-300"></span>
                                      <span className="text-gray-700 truncate">Övrigt</span>
                                    </span>
                                    <span className="text-gray-800 font-medium flex-shrink-0 ml-3">{formatAmount((row as any).ownMonthlyAverage)}/mån</span>
                                  </button>
                                  {ownOpen && ownItems.length > 0 && (
                                    <div className="ml-4 pl-3 border-l border-gray-200 mb-1">
                                      {ownItems.map((item: any, idx: number) => (
                                        <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                                          <span className="text-gray-500 truncate">{item.name}</span>
                                          <span className={`ml-3 flex-shrink-0 ${item.monthlyAverage < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                                            {item.monthlyAverage < 0 ? '−' : ''}{formatAmount(Math.abs(item.monthlyAverage))}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        {stats.months.map(m => (
                          <div key={m} className="flex items-center py-1">
                            <span className="text-sm text-gray-600 w-16">{formatMonth(m)}</span>
                            <div className="flex-1 mx-3 bg-gray-200 rounded-full h-2 overflow-hidden">
                              <div
                                className="h-2 rounded-full"
                                style={{
                                  width: `${((row.monthly[m] || 0) / maxMonthly) * 100}%`,
                                  backgroundColor: row.color || '#3B82F6'
                                }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium text-gray-900 w-24 text-right">
                              {formatAmount(row.monthly[m] || 0)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {(stats.tags || []).length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-1">Taggar</h2>
                <p className="text-sm text-gray-500 mb-3">
                  Vad kostar något totalt, tvärs över alla kategorier — en transaktion kan ha flera taggar.
                </p>
                <div className="space-y-2">
                  {stats.tags!.map(tag => {
                    const isOpen = expandedTags.has(tag.id);
                    const tagMax = Math.max(...stats.months.map(m => tag.monthly[m] || 0), 1);
                    return (
                      <div key={tag.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <button
                          onClick={() => setExpandedTags(prev => {
                            const next = new Set(prev);
                            if (next.has(tag.id)) next.delete(tag.id); else next.add(tag.id);
                            return next;
                          })}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center">
                            <span
                              className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full text-white mr-3"
                              style={{ backgroundColor: tag.color || '#0EA5E9' }}
                            >
                              {tag.name}
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">{formatAmount(tag.monthlyAverage)}<span className="text-xs font-normal text-gray-500">/mån</span></p>
                            <p className="text-xs text-gray-500">{formatAmount(tag.total)} totalt · {isOpen ? 'dölj ▲' : 'visa ▼'}</p>
                          </div>
                        </button>
                        {isOpen && (
                          <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
                            {tag.topItems.length > 0 && (
                              <div className="mb-3">
                                <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Största posterna · snitt/mån</p>
                                {tag.topItems.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                                    <span className="text-gray-600 truncate">{item.name}</span>
                                    <span className={`ml-3 flex-shrink-0 ${item.monthlyAverage < 0 ? 'text-green-600' : 'text-gray-800'}`}>
                                      {item.monthlyAverage < 0 ? '−' : ''}{formatAmount(Math.abs(item.monthlyAverage))}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {stats.months.map(m => (
                              <div key={m} className="flex items-center py-1">
                                <span className="text-sm text-gray-600 w-16">{formatMonth(m)}</span>
                                <div className="flex-1 mx-3 bg-gray-200 rounded-full h-2 overflow-hidden">
                                  <div
                                    className="h-2 rounded-full"
                                    style={{
                                      width: `${((tag.monthly[m] || 0) / tagMax) * 100}%`,
                                      backgroundColor: tag.color || '#0EA5E9'
                                    }}
                                  ></div>
                                </div>
                                <span className="text-sm font-medium text-gray-900 w-24 text-right">
                                  {formatAmount(tag.monthly[m] || 0)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {stats.uncategorized.monthlyAverage > 0 && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
                <p className="text-sm text-yellow-800">
                  <Info size={13} className="inline mr-1 -mt-0.5" /> {formatAmount(stats.uncategorized.monthlyAverage)}/mån är okategoriserat.
                  Skapa fler <button onClick={() => navigate('/settings/rules')} className="underline font-medium">regler</button> för
                  en mer exakt bild.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AnalysisPage;
